import { useEffect, useMemo } from 'react';

import { useDeviceType } from '@braskai/ui-kit';

import { useBalanceStore } from '@/entities/balance';
import { useClientStorageItem } from '@/entities/clientStorage';

import {
  BANNER_SCHEDULE,
  BannerStep,
  SCREEN_RECORD_PROMO_STORAGE_KEY,
  ScreenRecordPromoState,
} from '../lib/constants';
import { useScreenRecordPromoBannerStore } from './useScreenRecordPromoBannerStore';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const getDaysDifference = (date1: Date, date2: Date): number => {
  const diffTime = date2.getTime() - date1.getTime();
  return Math.floor(diffTime / MILLISECONDS_PER_DAY);
};

const parseDate = (isoString: string | undefined): Date | null => {
  if (!isoString) return null;
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseState = (value: string | undefined): ScreenRecordPromoState => {
  if (!value) return {};
  try {
    return JSON.parse(value) as ScreenRecordPromoState;
  } catch {
    return {};
  }
};

export const useScreenRecordPromoBanner = () => {
  const { isMobile } = useDeviceType();
  const { hasActiveSubscription } = useBalanceStore();
  const open = useScreenRecordPromoBannerStore((state) => state.open);

  const isEnabled = hasActiveSubscription && !isMobile;

  const { data: storageData, isLoading } = useClientStorageItem(SCREEN_RECORD_PROMO_STORAGE_KEY, {
    enabled: isEnabled,
  });

  const promoState = useMemo(() => parseState(storageData?.value), [storageData?.value]);

  const currentStep: BannerStep = useMemo(() => {
    if (isMobile || isLoading || !hasActiveSubscription) return null;

    const modalClosedAt = parseDate(promoState.modalClosedAt);
    if (!modalClosedAt) return null;

    const step1ClosedAt = parseDate(promoState.bannerStep1ClosedAt);
    const step2ClosedAt = parseDate(promoState.bannerStep2ClosedAt);
    const step3ClosedAt = parseDate(promoState.bannerStep3ClosedAt);

    const now = new Date();

    if (step3ClosedAt) return null;

    if (step2ClosedAt) {
      const daysSinceStep2 = getDaysDifference(step2ClosedAt, now);
      if (daysSinceStep2 >= BANNER_SCHEDULE.step3DelayDays) return 'step3';
      return null;
    }

    if (step1ClosedAt) {
      const daysSinceStep1 = getDaysDifference(step1ClosedAt, now);
      if (daysSinceStep1 >= BANNER_SCHEDULE.step2DelayDays) return 'step2';
      return null;
    }

    const daysSinceModalClosed = getDaysDifference(modalClosedAt, now);
    if (daysSinceModalClosed >= BANNER_SCHEDULE.step1DelayDays) return 'step1';

    return null;
  }, [isMobile, isLoading, hasActiveSubscription, promoState]);

  useEffect(() => {
    if (isMobile || isLoading || !hasActiveSubscription) return;

    if (currentStep !== null) {
      open();
    }
  }, [isMobile, isLoading, hasActiveSubscription, currentStep, open]);
};
